import React, { useState, useRef, useEffect, useCallback } from "react";
import { API } from "@/App";
import { 
  X, Maximize2, Minimize2, Send, Loader2, Zap, Target,
  Crosshair, Shield, AlertTriangle, FileText,
  DollarSign, CloudRain, UserCheck, Receipt, CheckCircle,
  Bell, Terminal, MapPin, Gem, Radio, Command, ChevronDown
} from "lucide-react";

// ============== AI COMMANDING OFFICER ==============
// Overwatch Command Interface - Central authority for system-wide decisions
// OVERWATCH sees. AI CO decides.

export default function OverwatchHUD({ estimateData, onAddToEstimate, onClose }) {
  const [isMaximized, setIsMaximized] = useState(false);
  const [activeTab, setActiveTab] = useState("brief"); // brief, directives, alerts, execute
  
  // COMMAND BRIEF & Alerts state
  const [sitrep, setSitrep] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [loadingAlerts, setLoadingAlerts] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  
  // Active Directives state
  const [directives, setDirectives] = useState([
    {
      id: "dir-001",
      project: "SMITH RESIDENCE",
      message: "Change order required before additional scope proceeds. Job state advancement is temporarily locked.",
      status: "ISSUED",
      timestamp: new Date().toISOString(),
    }
  ]);
  
  // Execute Command state
  const [commandText, setCommandText] = useState("");
  const [commandProcessing, setCommandProcessing] = useState(false);
  const [commandResult, setCommandResult] = useState(null);
  
  // Command Log state (replaces chat messages)
  const [commandLog, setCommandLog] = useState([
    {
      id: "init",
      type: "system",
      content: "AI COMMANDING OFFICER -- DAILY BRIEF\n\nOverwatch systems are active.\nAll operational data streams are being monitored in real time.\n\nSystems Online:\n- Scope Integrity Monitor\n- Cash Flow & Draw Control\n- Schedule & Delay Detection\n- Margin Protection Engine\n- Client Sentiment Monitor\n\nSpecialized Watch Modules:\n- GPS Asset Tracking\n- Voice & Field Signal Monitoring\n- Market & Pricing Intelligence\n\nCurrent Assessment:\nNo mission-critical failures detected.\nDeviations will be flagged immediately.",
      timestamp: new Date().toISOString(),
    }
  ]);
  const [isProcessing, setIsProcessing] = useState(false);
  
  // Expanded sections state for command briefs
  const [expandedSections, setExpandedSections] = useState({});
  
  const hudRef = useRef(null);
  const commandLogRef = useRef(null);
  
  // ═══════════════════════════════════════════════════════════════════════════
  // DISMISS THREAT: Mark threat as resolved (soft delete)
  // ═══════════════════════════════════════════════════════════════════════════
  const [dismissingAlert, setDismissingAlert] = useState(null);
  
  const dismissThreat = async (alertId) => {
    setDismissingAlert(alertId);
    try {
      await API.post(`/overwatch/dismiss-threat/${alertId}`);
      
      setAlerts(prev => prev.filter(a => a.alert_id !== alertId));
      
      if (sitrep) {
        setSitrep(prev => ({
          ...prev,
          top_alerts: prev.top_alerts?.filter(a => a.alert_id !== alertId) || [],
          alerts_summary: {
            ...prev.alerts_summary,
            total: Math.max(0, (prev.alerts_summary?.total || 1) - 1)
          }
        }));
      }
      
      // Log to command log
      setCommandLog(prev => [...prev, {
        id: `dismiss-${Date.now()}`,
        type: "system",
        content: `THREAT RESOLVED\n\nAlert ${alertId} has been dismissed and flagged as handled.\nStatus updated in operational database.`,
        timestamp: new Date().toISOString(),
      }]);
      
    } catch (error) {
      console.error('Failed to dismiss threat:', error);
    } finally {
      setDismissingAlert(null);
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // ACKNOWLEDGE DIRECTIVE: Mark directive as acknowledged
  // ═══════════════════════════════════════════════════════════════════════════
  const acknowledgeDirective = (directiveId) => {
    setDirectives(prev => prev.map(d => 
      d.id === directiveId ? { ...d, status: "ACKNOWLEDGED" } : d
    ));
  };
  
  const resolveDirective = (directiveId) => {
    setDirectives(prev => prev.map(d => 
      d.id === directiveId ? { ...d, status: "RESOLVED" } : d
    ));
  };

  // Fetch SITREP on mount
  useEffect(() => {
    fetchSitrep();
  }, []);

  const fetchSitrep = async () => {
    setLoadingAlerts(true);
    try {
      const response = await API.get("/overwatch/sitrep");
      setSitrep(response.data);
      setAlerts(response.data.top_alerts || []);
    } catch (error) {
      console.error("Failed to fetch SITREP:", error);
    } finally {
      setLoadingAlerts(false);
    }
  };

  const fetchAllAlerts = async () => {
    setLoadingAlerts(true);
    try {
      const response = await API.get("/overwatch/alerts");
      setAlerts(response.data.alerts || []);
      setSitrep(prev => ({ ...prev, alerts_summary: response.data.summary }));
    } catch (error) {
      console.error("Failed to fetch alerts:", error);
    } finally {
      setLoadingAlerts(false);
    }
  };

  const executeAction = async (alert) => {
    setActionLoading(alert.alert_id);
    try {
      const response = await API.post(`/overwatch/action/${alert.alert_id}`, {
        action_type: alert.action_type
      });
      
      // Log to command log
      setCommandLog(prev => [...prev, {
        id: `action-${Date.now()}`,
        type: "system",
        content: `ACTION EXECUTED\n\n${response.data.message}\nStatus: Complete.`,
        timestamp: new Date().toISOString(),
      }]);
      
      // Refresh alerts
      await fetchSitrep();
    } catch (error) {
      console.error("Action failed:", error);
    } finally {
      setActionLoading(null);
    }
  };

  // Auto-scroll command log
  useEffect(() => {
    if (commandLogRef.current) {
      commandLogRef.current.scrollTop = commandLogRef.current.scrollHeight;
    }
  }, [commandLog]);

  // Handle Command Brief refresh
  const handleBriefRefresh = useCallback(async () => {
    setIsProcessing(true);
    await fetchSitrep();
    
    if (sitrep) {
      const briefEntry = {
        id: `brief-${Date.now()}`,
        type: "system",
        content: `COMMAND ASSESSMENT UPDATE\n\nThreat Level: ${sitrep.threat_level}\nActive Alerts: ${sitrep.alerts_summary?.total || 0}\n- Critical: ${sitrep.alerts_summary?.critical || 0}\n- Warning: ${sitrep.alerts_summary?.warning || 0}\n\nFinancial Position:\n- Accounts Receivable: $${(sitrep.financial?.accounts_receivable || 0).toLocaleString()}\n- Accounts Payable: $${(sitrep.financial?.accounts_payable || 0).toLocaleString()}\n- Net Position: $${(sitrep.financial?.net_position || 0).toLocaleString()}\n\nOperational Status:\n- Tasks Scheduled: ${sitrep.operations?.tasks_scheduled || 0}\n- Unconfirmed Subcontractors: ${sitrep.operations?.unconfirmed_subs || 0}\n\nReview ALERTS tab for detailed threat breakdown.`,
        timestamp: new Date().toISOString(),
      };
      setCommandLog(prev => [...prev, briefEntry]);
    }
    setIsProcessing(false);
    setActiveTab("brief");
  }, [sitrep]);

  // State for Command Brief data
  const [commandBrief, setCommandBrief] = useState(null);
  const [showingBrief, setShowingBrief] = useState(false);

  // Handle Execute Command - ZERO LATENCY with COMMAND BRIEF
  const handleExecuteCommand = useCallback(async () => {
    if (!commandText.trim()) return;
    
    // Log the user command
    const userEntry = {
      id: `cmd-${Date.now()}`,
      type: "command",
      content: commandText,
      timestamp: new Date().toISOString(),
    };
    setCommandLog(prev => [...prev, userEntry]);
    const query = commandText;
    setCommandText("");
    setCommandProcessing(true);
    
    // Check if this is a job status query
    const lowerQuery = query.toLowerCase();
    const isJobQuery = lowerQuery.includes("smith") || 
                       lowerQuery.includes("miller") || 
                       lowerQuery.includes("johnson") ||
                       lowerQuery.includes("what's going on") ||
                       lowerQuery.includes("status on") ||
                       lowerQuery.includes("tell me about");
    
    if (isJobQuery) {
      // Call backend Command Brief API
      try {
        const response = await API.post("/telemetry/command-brief", { query });
        const brief = response.data;
        setCommandBrief(brief);
        setShowingBrief(true);
        
        // Add brief summary to command log
        setCommandLog(prev => [...prev, {
          id: `brief-${Date.now()}`,
          type: "brief",
          content: brief,
          timestamp: new Date().toISOString(),
        }]);
        setCommandProcessing(false);
        return;
      } catch (error) {
        console.error("Command Brief API error:", error);
        // Fall back to demo data
      }
    }
    
    // ZERO LATENCY - Return immediate data for non-job queries
    setTimeout(() => {
      let responseContent = "";
      
      if (lowerQuery.includes("mission") || lowerQuery.includes("all")) {
        responseContent = `MISSION STATUS: ALL ACTIVE PROJECTS
════════════════════════════════════════
Active Jobs: 8
Total Budget: $542,000
Total Collected: $385,000

⚠️ TOP RISKS:
   • 3 invoices overdue > 30 days
   • 2 permits pending approval
   • 1 subcontractor unconfirmed

📊 FINANCIAL HEALTH:
   • Avg Margin: 16.8%
   • Cash Position: $127,500
   • A/R Outstanding: $157,000`;
      } else if (lowerQuery.includes("authorize") || lowerQuery.includes("approve")) {
        responseContent = `AUTHORIZATION PROCESSED
════════════════════════════════════════
Action: Job advancement authorized
Status: APPROVED
Timestamp: ${new Date().toLocaleString()}

Job state updated to: PRODUCTION
Notifications sent to: Field team, PM`;
      } else {
        responseContent = `QUERY EXECUTED
════════════════════════════════════════
No matching targets found for: "${query}"

Available commands:
• "What's going on with [Job Name]"
• "Status on [Client] job"
• "Show mission status"
• "Authorize [action]"`;
      }
      
      setCommandLog(prev => [...prev, {
        id: `response-${Date.now()}`,
        type: "system",
        content: responseContent,
        timestamp: new Date().toISOString(),
      }]);
      setCommandProcessing(false);
    }, 500); // Faster response time
  }, [commandText]);

  // Get module icon
  const getModuleIcon = (module) => {
    const icons = {
      scope_hunter: <Target className="w-4 h-4" />,
      cash_flow: <DollarSign className="w-4 h-4" />,
      weather: <CloudRain className="w-4 h-4" />,
      enforcer: <UserCheck className="w-4 h-4" />,
      forensic: <Receipt className="w-4 h-4" />,
      sentiment: <FileText className="w-4 h-4" />,
      watchtower: <MapPin className="w-4 h-4" />,
      intel_scout: <Gem className="w-4 h-4" />,
    };
    return icons[module] || <AlertTriangle className="w-4 h-4" />;
  };

  // Get severity color - SOFTER PALETTE
  const getSeverityColor = (severity) => {
    if (severity === "critical") return "bg-rose-500/15 border-rose-500/40 text-rose-300";
    if (severity === "warning") return "bg-orange-500/15 border-orange-500/40 text-orange-300";
    return "bg-sky-500/15 border-sky-500/40 text-sky-300"; // info
  };

  const hudStyle = isMaximized
    ? { top: 0, left: 0, right: 0, bottom: 0, width: "100%", height: "100%" }
    : { right: 16, top: 70, maxHeight: 'calc(100vh - 140px)' };

  return (
    <div
      ref={hudRef}
      className={`fixed z-[9999] ${isMaximized ? "" : "w-[420px]"} flex flex-col rounded-xl overflow-hidden
        bg-slate-900/95 backdrop-blur-xl border border-emerald-500/50 shadow-2xl shadow-emerald-500/10`}
      style={{
        ...hudStyle,
        paddingBottom: '20px', // Clearance for 'Made with Emergent' badge
      }}
    >
      {/* ========== HEADER - AI COMMANDING OFFICER ========== */}
      <div className="flex items-center justify-between px-3 py-2.5 bg-slate-950/90 border-b border-emerald-500/30">
        <div className="flex flex-col">
          <div className="flex items-center gap-2">
            <Command className="w-4 h-4 text-emerald-400" />
            <span className="text-xs font-bold text-emerald-400 tracking-wider">AI COMMANDING OFFICER</span>
          </div>
          <span className="text-xs text-slate-500 ml-6">Overwatch Command Interface</span>
        </div>
        
        <div className="flex items-center gap-2">
          {/* COMMAND STATUS BADGE */}
          <div className={`flex items-center gap-1.5 px-2 py-1 rounded text-xs font-bold ${
            sitrep?.threat_level === "HIGH" ? "bg-red-500/20 border border-red-500/40 text-red-300" :
            sitrep?.threat_level === "ELEVATED" ? "bg-amber-500/20 border border-amber-500/40 text-amber-300" :
            "bg-emerald-500/20 border border-emerald-500/40 text-emerald-300"
          }`}>
            <span className={`w-1.5 h-1.5 rounded-full animate-pulse ${
              sitrep?.threat_level === "HIGH" ? "bg-red-400" :
              sitrep?.threat_level === "ELEVATED" ? "bg-amber-400" :
              "bg-emerald-400"
            }`} />
            <span>COMMAND STATUS:</span>
            <span className="font-black">
              {sitrep?.threat_level === "HIGH" ? "HIGH" :
               sitrep?.threat_level === "ELEVATED" ? "ELEVATED" : "ACTIVE"}
            </span>
          </div>
          
          <button
            onClick={() => setIsMaximized(!isMaximized)}
            className="p-1 text-slate-400 hover:text-white hover:bg-slate-800 rounded transition-colors"
          >
            {isMaximized ? <Minimize2 className="w-3.5 h-3.5" /> : <Maximize2 className="w-3.5 h-3.5" />}
          </button>
          <button
            onClick={onClose}
            className="p-1 text-slate-400 hover:text-red-400 hover:bg-slate-800 rounded transition-colors"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* ========== TABS - Command Interface ========== */}
      <div className="flex border-b border-slate-800">
        {[
          { id: "brief", label: "COMMAND BRIEF", icon: <FileText className="w-3 h-3" /> },
          { id: "directives", label: `ACTIVE DIRECTIVES ${directives.filter(d => d.status !== "RESOLVED").length > 0 ? `(${directives.filter(d => d.status !== "RESOLVED").length})` : ""}`, icon: <Target className="w-3 h-3" /> },
          { id: "alerts", label: `ALERTS ${sitrep?.alerts_summary?.total ? `(${sitrep.alerts_summary.total})` : ""}`, icon: <Bell className="w-3 h-3" /> },
          { id: "execute", label: "EXECUTE", icon: <Terminal className="w-3 h-3" /> },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => {
              setActiveTab(tab.id);
              if (tab.id === "alerts") fetchAllAlerts();
            }}
            className={`flex-1 flex items-center justify-center gap-1 px-2 py-2 text-xs font-semibold transition-colors ${
              activeTab === tab.id
                ? "bg-emerald-500/20 text-emerald-300 border-b-2 border-emerald-500"
                : "text-slate-400 hover:text-white hover:bg-slate-800/50"
            }`}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* ========== CONTENT ========== */}
      <div className="flex-1 overflow-y-auto min-h-0">
        {/* COMMAND BRIEF TAB */}
        {activeTab === "brief" && (
          <div className="p-3 space-y-3">
            {loadingAlerts ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 text-emerald-400 animate-spin" />
              </div>
            ) : (
              <>
                {/* Daily Brief Header */}
                <div className="p-3 rounded-lg bg-slate-800/60 border border-emerald-500/30">
                  <div className="flex items-center gap-2 mb-3">
                    <Command className="w-4 h-4 text-emerald-400" />
                    <span className="text-xs font-bold text-emerald-300 tracking-wide">AI COMMANDING OFFICER -- DAILY BRIEF</span>
                  </div>
                  <p className="text-xs text-slate-300 leading-relaxed">
                    Overwatch systems are active.<br/>
                    All operational data streams are being monitored in real time.
                  </p>
                </div>

                {/* Systems Online */}
                <div className="p-3 rounded-lg bg-slate-800/50 border border-slate-700">
                  <div className="text-xs text-slate-400 uppercase tracking-wider mb-2">Systems Online</div>
                  <div className="space-y-1.5">
                    {[
                      "Scope Integrity Monitor",
                      "Cash Flow & Draw Control",
                      "Schedule & Delay Detection",
                      "Margin Protection Engine",
                      "Client Sentiment Monitor"
                    ].map((system, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs text-emerald-300">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                        {system}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Specialized Watch Modules */}
                <div className="p-3 rounded-lg bg-slate-800/50 border border-slate-700">
                  <div className="text-xs text-slate-400 uppercase tracking-wider mb-2">Specialized Watch Modules</div>
                  <div className="space-y-1.5">
                    {[
                      "GPS Asset Tracking",
                      "Voice & Field Signal Monitoring",
                      "Market & Pricing Intelligence"
                    ].map((module, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs text-slate-300">
                        <span className="w-1.5 h-1.5 rounded-full bg-slate-400" />
                        {module}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Current Assessment */}
                <div className={`p-3 rounded-lg border ${
                  sitrep?.threat_level === "HIGH" ? "bg-red-500/10 border-red-500/30" :
                  sitrep?.threat_level === "ELEVATED" ? "bg-amber-500/10 border-amber-500/30" :
                  "bg-emerald-500/10 border-emerald-500/30"
                }`}>
                  <div className="text-xs text-slate-400 uppercase tracking-wider mb-2">Current Assessment</div>
                  <p className={`text-xs leading-relaxed ${
                    sitrep?.threat_level === "HIGH" ? "text-red-300" :
                    sitrep?.threat_level === "ELEVATED" ? "text-amber-300" :
                    "text-emerald-300"
                  }`}>
                    {sitrep?.threat_level === "HIGH" 
                      ? "CRITICAL ISSUES DETECTED.\nImmediate attention required. Review ALERTS tab."
                      : sitrep?.threat_level === "ELEVATED"
                      ? "Elevated risk conditions detected.\nMonitor ALERTS tab for developing situations."
                      : "No mission-critical failures detected.\nDeviations will be flagged immediately."}
                  </p>
                  {sitrep && (
                    <div className="mt-2 pt-2 border-t border-slate-700/50 grid grid-cols-2 gap-2 text-xs">
                      <div className="text-slate-400">Active Alerts: <span className="text-white font-semibold">{sitrep.alerts_summary?.total || 0}</span></div>
                      <div className="text-slate-400">Critical: <span className="text-red-300 font-semibold">{sitrep.alerts_summary?.critical || 0}</span></div>
                    </div>
                  )}
                </div>

                {/* View Full Assessment Button */}
                <button
                  onClick={handleBriefRefresh}
                  disabled={isProcessing}
                  className="w-full py-2.5 px-4 bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-500/40 
                    rounded-lg text-xs font-semibold text-emerald-300 transition-colors disabled:opacity-50
                    flex items-center justify-center gap-2"
                >
                  {isProcessing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Target className="w-3.5 h-3.5" />}
                  VIEW FULL COMMAND ASSESSMENT
                </button>
              </>
            )}
          </div>
        )}

        {/* ACTIVE DIRECTIVES TAB */}
        {activeTab === "directives" && (
          <div className="p-3 space-y-2">
            {directives.filter(d => d.status !== "RESOLVED").length > 0 ? (
              directives.filter(d => d.status !== "RESOLVED").map(directive => (
                <div
                  key={directive.id}
                  className={`p-3 rounded-lg border ${
                    directive.status === "ISSUED" 
                      ? "bg-amber-500/10 border-amber-500/30" 
                      : "bg-emerald-500/10 border-emerald-500/30"
                  }`}
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="flex items-center gap-2">
                      <Target className="w-4 h-4 text-amber-400" />
                      <span className="text-xs font-bold text-white">DIRECTIVE ISSUED</span>
                    </div>
                    <span className={`px-1.5 py-0.5 rounded text-[8px] font-bold ${
                      directive.status === "ISSUED" 
                        ? "bg-amber-500/30 text-amber-300" 
                        : "bg-emerald-500/30 text-emerald-300"
                    }`}>
                      {directive.status}
                    </span>
                  </div>
                  
                  <div className="text-xs text-slate-400 mb-1">{directive.project}</div>
                  <p className="text-xs text-slate-300 leading-relaxed mb-3">{directive.message}</p>
                  
                  <div className="flex items-center gap-2">
                    {directive.status === "ISSUED" && (
                      <button
                        onClick={() => acknowledgeDirective(directive.id)}
                        className="flex-1 py-1.5 px-3 bg-amber-500/20 hover:bg-amber-500/30 border border-amber-500/40 
                          rounded text-xs font-semibold text-amber-300 transition-colors"
                      >
                        ACKNOWLEDGE
                      </button>
                    )}
                    {directive.status === "ACKNOWLEDGED" && (
                      <button
                        onClick={() => resolveDirective(directive.id)}
                        className="flex-1 py-1.5 px-3 bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-500/40 
                          rounded text-xs font-semibold text-emerald-300 transition-colors"
                      >
                        MARK RESOLVED
                      </button>
                    )}
                  </div>
                  
                  <div className="text-xs text-slate-500 mt-2">
                    {new Date(directive.timestamp).toLocaleString()}
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8">
                <CheckCircle className="w-8 h-8 mx-auto mb-2 text-emerald-400/50" />
                <p className="text-xs text-slate-400">No active directives.</p>
                <p className="text-xs text-slate-500">All directives have been resolved.</p>
              </div>
            )}
          </div>
        )}

        {/* ALERTS TAB - COMMAND ALERTS */}
        {activeTab === "alerts" && (
          <div className="p-3 space-y-2">
            {/* Header */}
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-slate-400 uppercase tracking-wider">
                COMMAND ALERTS ({alerts.length})
              </span>
            </div>
            
            {loadingAlerts ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 text-emerald-400 animate-spin" />
              </div>
            ) : alerts.length > 0 ? (
              alerts.map(alert => (
                <div
                  key={alert.alert_id}
                  className={`p-3 rounded-lg border ${getSeverityColor(alert.severity)}`}
                >
                  <div className="flex items-start gap-2">
                    <div className="p-1.5 rounded bg-slate-900/50">
                      {getModuleIcon(alert.module)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <span className={`text-xs font-bold ${
                            alert.severity === "critical" ? "text-red-400" : "text-amber-400"
                          }`}>
                            {alert.severity === "critical" ? "RED" : "YELLOW"}
                          </span>
                          <span className="text-xs text-slate-300">--</span>
                          <span className="text-xs font-bold text-white">{alert.title}</span>
                        </div>
                        {/* Dismiss Button */}
                        <button
                          onClick={() => dismissThreat(alert.alert_id)}
                          disabled={dismissingAlert === alert.alert_id}
                          className="p-1 text-slate-500 hover:text-red-400 hover:bg-slate-800 rounded transition-colors"
                          title="Dismiss threat"
                        >
                          {dismissingAlert === alert.alert_id ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <X className="w-3.5 h-3.5" />
                          )}
                        </button>
                      </div>
                      <p className="text-xs text-slate-300 mt-1.5 leading-relaxed">{alert.description}</p>
                      
                      {/* Recommended Action */}
                      {alert.action_label && (
                        <div className="mt-2 pt-2 border-t border-slate-700/50">
                          <div className="text-xs text-slate-500 mb-1">Recommended Action:</div>
                          <button
                            onClick={() => executeAction(alert)}
                            disabled={actionLoading === alert.alert_id}
                            className="flex items-center gap-1 px-2 py-1 text-xs font-semibold bg-emerald-500/20 text-emerald-300 rounded hover:bg-emerald-500/30 border border-emerald-500/40 transition-colors disabled:opacity-50"
                          >
                            {actionLoading === alert.alert_id ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <CheckCircle className="w-3 h-3" />
                            )}
                            {alert.action_label}
                          </button>
                        </div>
                      )}
                      
                      {/* Escalation Required for Critical */}
                      {alert.severity === "critical" && (
                        <div className="mt-2 py-1 px-2 bg-red-500/10 rounded text-xs text-red-300 font-semibold">
                          ESCALATION REQUIRED
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8">
                <CheckCircle className="w-8 h-8 mx-auto mb-2 text-emerald-400/50" />
                <p className="text-xs text-slate-400">No active alerts.</p>
                <p className="text-xs text-slate-500">All systems operating within normal parameters.</p>
              </div>
            )}
          </div>
        )}

        {/* EXECUTE TAB - Command Interface */}
        {activeTab === "execute" && (
          <div className="flex flex-col h-full min-h-0">
            {/* Command Log - scrollable area */}
            <div ref={commandLogRef} className="flex-1 overflow-y-auto p-4 space-y-4 min-h-0">
              {commandLog.map(entry => {
                // ════════════════════════════════════════════════════════════════
                // THREE-LAYER COMMAND PRESENTATION MODEL
                // Layer 1: Commander's Assessment (ALWAYS EXPANDED)
                // Layer 2: Operational Summary (COLLAPSED BY DEFAULT)
                // Layer 3: Full Intelligence (EXPANDED ON DEMAND)
                // ════════════════════════════════════════════════════════════════
                if (entry.type === "brief" && entry.content) {
                  const b = entry.content;
                  const header = b.header || {};
                  const mission = b.mission_snapshot || {};
                  const schedule = b.schedule_reality || {};
                  const financial = b.financial_control || {};
                  const scope = b.scope_integrity || {};
                  const field = b.field_proof_of_life || {};
                  const risks = b.risk_register || [];
                  const dirs = b.command_directives || [];
                  const decisions = b.decisions_required || [];
                  const intel = b.supporting_intelligence || [];
                  
                  // Expanded state for each section
                  const briefId = entry.id;
                  const briefExpandedSections = expandedSections[briefId] || {
                    mission: false,
                    schedule: false,
                    financial: false,
                    scope: false,
                    field: false,
                    risks: false,
                    nonBlocking: false,
                    intel: false,
                  };
                  
                  const toggleSection = (section) => {
                    setExpandedSections(prev => ({
                      ...prev,
                      [briefId]: {
                        ...briefExpandedSections,
                        [section]: !briefExpandedSections[section]
                      }
                    }));
                  };
                  
                  const statusColor = header.status === "RED" ? "red" : header.status === "YELLOW" ? "amber" : "emerald";
                  
                  // Check for blocking directives
                  const blockingDirs = dirs.filter(d => d.blocking === "YES");
                  const nonBlockingDirs = dirs.filter(d => d.blocking !== "YES");
                  const hasBlocking = blockingDirs.length > 0;
                  
                  // Helper for "Awaiting system data" instead of "DATA NOT AVAILABLE"
                  const formatValue = (val) => {
                    if (!val || val === "DATA NOT AVAILABLE") return <span className="text-slate-500 italic">Awaiting system data</span>;
                    return val;
                  };
                  
                  // Get primary risk
                  const primaryRisk = risks[0];
                  const topRisks = risks.slice(0, 3);
                  const remainingRisks = risks.slice(3);
                  
                  return (
                    <div key={entry.id} className="rounded-xl border border-slate-700 bg-slate-950 overflow-hidden">
                      
                      {/* ════════════════════════════════════════════════════════════
                          LAYER 1 — COMMANDER'S ASSESSMENT (ALWAYS EXPANDED)
                          Max 5 lines. No tables. Readable in under 10 seconds.
                          Font: 18-20px, weight 600-700
                         ════════════════════════════════════════════════════════════ */}
                      <div className={`px-5 py-5 border-b-2 ${
                        statusColor === "red" ? "bg-gradient-to-r from-red-950/80 to-slate-950 border-red-500" :
                        statusColor === "amber" ? "bg-gradient-to-r from-amber-950/60 to-slate-950 border-amber-500" :
                        "bg-gradient-to-r from-emerald-950/60 to-slate-950 border-emerald-500"
                      }`} style={{ lineHeight: '1.5' }}>
                        <div className="text-xs text-slate-500 tracking-widest mb-3 font-medium">COMMANDER&apos;S ASSESSMENT</div>
                        
                        {/* Status Line - LARGEST */}
                        <div className="flex items-center gap-3 mb-3">
                          <span className={`text-lg font-bold ${
                            statusColor === "red" ? "text-red-400" :
                            statusColor === "amber" ? "text-amber-400" :
                            "text-emerald-400"
                          }`}>Status: {header.status}</span>
                          <span className="text-base text-slate-300">
                            ({statusColor === "red" ? "Requires immediate attention" : 
                              statusColor === "amber" ? "Minor issues require monitoring" : 
                              "Operations nominal"})
                          </span>
                        </div>
                        
                        {/* Primary Risk */}
                        <div className="text-base text-slate-200 mb-2" style={{ fontWeight: 500 }}>
                          <span className="text-slate-400">Primary Risk:</span>{" "}
                          {primaryRisk ? primaryRisk.risk : "No critical risks identified"}
                        </div>
                        
                        {/* Financial Exposure */}
                        <div className="text-base text-slate-200 mb-2" style={{ fontWeight: 500 }}>
                          <span className="text-slate-400">Financial Exposure:</span>{" "}
                          {financial.overdue_invoices && financial.overdue_invoices !== "NONE" 
                            ? financial.overdue_invoices 
                            : "No overdue balances"}
                        </div>
                        
                        {/* Operations */}
                        <div className="text-base text-slate-200 mb-2" style={{ fontWeight: 500 }}>
                          <span className="text-slate-400">Operations:</span>{" "}
                          {field.active_subs_on_site || "0"} subs on site, last activity {field.last_field_activity || "not reported"}
                        </div>
                        
                        {/* Immediate Orders */}
                        <div className="text-base text-slate-200" style={{ fontWeight: 500 }}>
                          <span className="text-slate-400">Immediate Orders:</span>{" "}
                          {hasBlocking ? (
                            <span className="text-red-400 font-semibold">{blockingDirs[0].directive}</span>
                          ) : (
                            <span className="text-emerald-400">None</span>
                          )}
                        </div>
                      </div>

                      {/* ════════════════════════════════════════════════════════════
                          LAYER 2 — OPERATIONAL SUMMARY (COLLAPSED BY DEFAULT)
                          One-line summaries, expandable. Font: 14-15px, weight 500
                         ════════════════════════════════════════════════════════════ */}
                      <div className="divide-y divide-slate-800/50">
                        
                        {/* Mission Snapshot Summary */}
                        <div className="px-5 py-3">
                          <button 
                            onClick={() => toggleSection('mission')}
                            className="w-full flex items-center justify-between text-left hover:bg-slate-900/50 -mx-2 px-2 py-1 rounded transition-colors"
                          >
                            <div className="flex items-center gap-3">
                              <span className="text-sm font-semibold text-slate-300">Mission Snapshot</span>
                              <span className="text-sm text-slate-400">
                                {formatValue(mission.contract_value)} contract | GP: {formatValue(mission.current_projected_gp)}
                              </span>
                            </div>
                            <ChevronDown className={`w-4 h-4 text-slate-500 transition-transform ${briefExpandedSections.mission ? 'rotate-180' : ''}`} />
                          </button>
                          {briefExpandedSections.mission && (
                            <div className="mt-3 pl-2 space-y-2 text-sm text-slate-400" style={{ lineHeight: '1.6' }}>
                              <div className="grid grid-cols-2 gap-4">
                                <div>Contract Value: <span className="text-slate-200">{formatValue(mission.contract_value)}</span></div>
                                <div>Original GP: <span className="text-slate-200">{formatValue(mission.original_projected_gp)}</span></div>
                                <div>Current GP: <span className="text-emerald-400">{formatValue(mission.current_projected_gp)}</span></div>
                                <div>Committed: <span className="text-amber-400">{formatValue(mission.committed_cost)}</span></div>
                                <div>Spent: <span className="text-red-400">{formatValue(mission.spent_to_date)}</span></div>
                                <div>Exposure: <span className="text-slate-200">{formatValue(mission.remaining_exposure)}</span></div>
                              </div>
                              <div className="pt-2 border-t border-slate-800 mt-2">
                                <div className="text-slate-500 mb-1">Cash Position:</div>
                                <div className="grid grid-cols-3 gap-2">
                                  <div>Invoiced: <span className="text-slate-200">{formatValue(mission.cash_position_invoiced)}</span></div>
                                  <div>Collected: <span className="text-emerald-400">{formatValue(mission.cash_position_collected)}</span></div>
                                  <div>Outstanding: <span className="text-red-400">{formatValue(mission.cash_position_outstanding)}</span></div>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Schedule Reality Summary */}
                        <div className="px-5 py-3">
                          <button 
                            onClick={() => toggleSection('schedule')}
                            className="w-full flex items-center justify-between text-left hover:bg-slate-900/50 -mx-2 px-2 py-1 rounded transition-colors"
                          >
                            <div className="flex items-center gap-3">
                              <span className="text-sm font-semibold text-slate-300">Schedule Reality</span>
                              <span className={`text-sm ${schedule.schedule_variance?.includes('-') ? 'text-red-400' : 'text-slate-400'}`}>
                                {schedule.schedule_variance || "On track"} | {schedule.critical_path_blockers?.length > 0 ? "Blocker active" : "No blockers"}
                              </span>
                            </div>
                            <ChevronDown className={`w-4 h-4 text-slate-500 transition-transform ${briefExpandedSections.schedule ? 'rotate-180' : ''}`} />
                          </button>
                          {briefExpandedSections.schedule && (
                            <div className="mt-3 pl-2 space-y-2 text-sm text-slate-400" style={{ lineHeight: '1.6' }}>
                              <div className="grid grid-cols-2 gap-4">
                                <div>Start: <span className="text-slate-200">{schedule.start_date}</span></div>
                                <div>Target: <span className="text-slate-200">{schedule.target_completion}</span></div>
                                <div>Days Remaining: <span className="text-slate-200">{schedule.days_remaining}</span></div>
                                <div>Variance: <span className={schedule.schedule_variance?.includes('-') ? 'text-red-400 font-semibold' : 'text-emerald-400'}>{schedule.schedule_variance}</span></div>
                              </div>
                              <div className="pt-2 border-t border-slate-800 mt-2">
                                <div className="text-slate-500 mb-2">Milestones:</div>
                                {schedule.milestones?.map((m, i) => (
                                  <div key={i} className="flex items-center gap-2 py-1">
                                    <span className={`w-2 h-2 rounded-full ${m.status?.includes('LATE') ? 'bg-red-500' : m.status?.includes('COMPLETED') ? 'bg-emerald-500' : 'bg-slate-600'}`} />
                                    <span className="text-slate-300">{m.name}</span>
                                    <span className="text-slate-500 ml-auto">{m.status}</span>
                                  </div>
                                ))}
                              </div>
                              {schedule.critical_path_blockers?.length > 0 && (
                                <div className="p-2 bg-red-500/10 border border-red-500/30 rounded mt-2">
                                  <div className="text-red-400 font-semibold">Critical Blockers:</div>
                                  {schedule.critical_path_blockers.map((b, i) => (
                                    <div key={i} className="text-red-300 text-sm">{b}</div>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Financial Control Summary */}
                        <div className="px-5 py-3">
                          <button 
                            onClick={() => toggleSection('financial')}
                            className="w-full flex items-center justify-between text-left hover:bg-slate-900/50 -mx-2 px-2 py-1 rounded transition-colors"
                          >
                            <div className="flex items-center gap-3">
                              <span className="text-sm font-semibold text-slate-300">Financial Control</span>
                              <span className={`text-sm ${financial.overdue_invoices !== 'NONE' ? 'text-red-400' : 'text-slate-400'}`}>
                                {financial.overdue_invoices !== 'NONE' ? financial.overdue_invoices : 'No overdue'} | {financial.pending_change_orders !== 'NONE' ? financial.pending_change_orders : 'No pending COs'}
                              </span>
                            </div>
                            <ChevronDown className={`w-4 h-4 text-slate-500 transition-transform ${briefExpandedSections.financial ? 'rotate-180' : ''}`} />
                          </button>
                          {briefExpandedSections.financial && (
                            <div className="mt-3 pl-2 space-y-2 text-sm text-slate-400" style={{ lineHeight: '1.6' }}>
                              <div className="grid grid-cols-2 gap-4">
                                <div>Budgeted: <span className="text-slate-200">{formatValue(financial.budgeted)}</span></div>
                                <div>Committed: <span className="text-amber-400">{formatValue(financial.committed)}</span></div>
                                <div>Spent: <span className="text-red-400">{formatValue(financial.spent)}</span></div>
                                <div>Uncommitted Risk: <span className="text-slate-200">{formatValue(financial.uncommitted_risk)}</span></div>
                              </div>
                              <div className="pt-2 border-t border-slate-800 mt-2">
                                <div className="text-slate-500 mb-1">Open Issues:</div>
                                <div>Overdue Invoices: <span className={financial.overdue_invoices === 'NONE' ? 'text-emerald-400' : 'text-red-400'}>{financial.overdue_invoices}</span></div>
                                <div>Pending COs: <span className="text-amber-400">{financial.pending_change_orders}</span></div>
                                <div>Allowances at Risk: {formatValue(financial.allowances_at_risk)}</div>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Scope Integrity Summary */}
                        <div className="px-5 py-3">
                          <button 
                            onClick={() => toggleSection('scope')}
                            className="w-full flex items-center justify-between text-left hover:bg-slate-900/50 -mx-2 px-2 py-1 rounded transition-colors"
                          >
                            <div className="flex items-center gap-3">
                              <span className="text-sm font-semibold text-slate-300">Scope Integrity</span>
                              <span className={`text-sm ${scope.scope_drift_status === 'SEVERE' ? 'text-red-400' : scope.scope_drift_status === 'DETECTED' ? 'text-amber-400' : 'text-slate-400'}`}>
                                {scope.pending_implied_changes?.length || 0} unsigned CO{scope.pending_implied_changes?.length !== 1 ? 's' : ''} | Drift: {scope.scope_drift_status || 'NONE'}
                              </span>
                            </div>
                            <ChevronDown className={`w-4 h-4 text-slate-500 transition-transform ${briefExpandedSections.scope ? 'rotate-180' : ''}`} />
                          </button>
                          {briefExpandedSections.scope && (
                            <div className="mt-3 pl-2 space-y-2 text-sm text-slate-400" style={{ lineHeight: '1.6' }}>
                              <div>Original Scope: <span className="text-slate-300">{scope.original_scope_summary}</span></div>
                              {scope.approved_changes?.length > 0 && (
                                <div className="pt-2">
                                  <div className="text-emerald-400 mb-1">Approved Changes:</div>
                                  {scope.approved_changes.map((co, i) => (
                                    <div key={i} className="text-slate-300">{co.co_number} | {co.description} | {co.value}</div>
                                  ))}
                                </div>
                              )}
                              {scope.pending_implied_changes?.length > 0 && (
                                <div className="pt-2">
                                  <div className="text-amber-400 mb-1">Pending/Implied:</div>
                                  {scope.pending_implied_changes.map((ch, i) => (
                                    <div key={i} className="text-slate-300">{ch.description} | {ch.estimated_value} | Risk: {ch.risk_level}</div>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Field Proof-of-Life Summary */}
                        <div className="px-5 py-3">
                          <button 
                            onClick={() => toggleSection('field')}
                            className="w-full flex items-center justify-between text-left hover:bg-slate-900/50 -mx-2 px-2 py-1 rounded transition-colors"
                          >
                            <div className="flex items-center gap-3">
                              <span className="text-sm font-semibold text-slate-300">Field Proof-of-Life</span>
                              <span className={`text-sm ${field.missed_checkins_24h !== '0' ? 'text-red-400' : 'text-slate-400'}`}>
                                {field.active_subs_on_site} active | Last activity: {field.last_field_activity || 'Not reported'}
                              </span>
                            </div>
                            <ChevronDown className={`w-4 h-4 text-slate-500 transition-transform ${briefExpandedSections.field ? 'rotate-180' : ''}`} />
                          </button>
                          {briefExpandedSections.field && (
                            <div className="mt-3 pl-2 space-y-2 text-sm text-slate-400" style={{ lineHeight: '1.6' }}>
                              <div className="grid grid-cols-2 gap-4">
                                <div>Last Activity: <span className="text-slate-200">{field.last_field_activity}</span></div>
                                <div>Last Photo: <span className="text-slate-200">{field.last_photo_upload}</span></div>
                                <div>Last Daily Log: <span className="text-slate-200">{field.last_daily_log}</span></div>
                                <div>Subs On Site: <span className="text-emerald-400">{field.active_subs_on_site}</span></div>
                              </div>
                              <div>Missed Check-ins (24h): <span className={field.missed_checkins_24h === '0' ? 'text-emerald-400' : 'text-red-400'}>{field.missed_checkins_24h}</span></div>
                              {field.latest_field_note && (
                                <div className="p-2 bg-slate-900/50 rounded italic text-slate-400 mt-2">
                                  &quot;{field.latest_field_note}&quot;
                                </div>
                              )}
                            </div>
                          )}
                        </div>

                        {/* ════════════════════════════════════════════════════════════
                            RISK REGISTER - TOP 3 ONLY, expandable
                           ════════════════════════════════════════════════════════════ */}
                        <div className="px-5 py-3">
                          <div className="text-sm font-semibold text-slate-300 mb-3">Risk Register (Top 3)</div>
                          {topRisks.map((risk, i) => (
                            <div key={i} className="flex items-center gap-2 py-1.5 text-sm">
                              <span className={`px-2 py-0.5 rounded text-xs font-bold ${
                                risk.severity === 'HIGH' ? 'bg-red-500/20 text-red-400' :
                                risk.severity === 'MEDIUM' ? 'bg-amber-500/20 text-amber-400' :
                                'bg-slate-700 text-slate-400'
                              }`}>{risk.severity}</span>
                              <span className="text-slate-300">{risk.risk}</span>
                            </div>
                          ))}
                          {remainingRisks.length > 0 && (
                            <button 
                              onClick={() => toggleSection('risks')}
                              className="text-sm text-emerald-400 hover:text-emerald-300 mt-2 flex items-center gap-1"
                            >
                              {briefExpandedSections.risks ? 'Hide' : 'View'} Full Risk Register ({remainingRisks.length} more)
                              <ChevronDown className={`w-3 h-3 transition-transform ${briefExpandedSections.risks ? 'rotate-180' : ''}`} />
                            </button>
                          )}
                          {briefExpandedSections.risks && remainingRisks.map((risk, i) => (
                            <div key={i} className="flex items-center gap-2 py-1.5 text-sm mt-1">
                              <span className={`px-2 py-0.5 rounded text-xs font-bold ${
                                risk.severity === 'HIGH' ? 'bg-red-500/20 text-red-400' :
                                risk.severity === 'MEDIUM' ? 'bg-amber-500/20 text-amber-400' :
                                'bg-slate-700 text-slate-400'
                              }`}>{risk.severity}</span>
                              <span className="text-slate-300">{risk.risk}</span>
                            </div>
                          ))}
                        </div>

                        {/* ════════════════════════════════════════════════════════════
                            COMMAND DIRECTIVES - BLOCKING always visible, non-blocking collapsed
                           ════════════════════════════════════════════════════════════ */}
                        <div className="px-5 py-4">
                          <div className="text-sm font-semibold text-slate-300 mb-3">Command Directives</div>
                          
                          {/* BLOCKING ORDERS - Always visible, red */}
                          {blockingDirs.length > 0 && (
                            <div className="mb-4">
                              <div className="text-xs text-red-400 font-bold tracking-wider mb-2">BLOCKING ORDERS</div>
                              {blockingDirs.map((dir, i) => (
                                <div key={i} className="p-3 bg-red-500/10 border border-red-500/40 rounded-lg mb-2">
                                  <div className="text-sm text-red-300 font-semibold mb-1">{dir.directive}</div>
                                  <div className="text-xs text-red-400/80">
                                    Owner: {dir.owner || 'Unassigned'} | Due: {dir.due || 'TBD'}
                                  </div>
                                  {dir.blocking_description && (
                                    <div className="text-xs text-red-300 mt-2 pt-2 border-t border-red-500/30">
                                      ⚠️ {dir.blocking_description}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                          
                          {/* NON-BLOCKING TASKS - Collapsed by default */}
                          {nonBlockingDirs.length > 0 && (
                            <div>
                              <button 
                                onClick={() => toggleSection('nonBlocking')}
                                className="text-sm text-slate-400 hover:text-slate-300 flex items-center gap-1"
                              >
                                Non-Blocking Tasks ({nonBlockingDirs.length})
                                <ChevronDown className={`w-3 h-3 transition-transform ${briefExpandedSections.nonBlocking ? 'rotate-180' : ''}`} />
                              </button>
                              {briefExpandedSections.nonBlocking && nonBlockingDirs.map((dir, i) => (
                                <div key={i} className="p-2 bg-slate-800/50 border border-slate-700 rounded mt-2">
                                  <div className="text-sm text-slate-300">{dir.directive}</div>
                                  <div className="text-xs text-slate-500 mt-1">
                                    Owner: {dir.owner || 'Unassigned'} | Due: {dir.due || 'TBD'}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                          
                          {blockingDirs.length === 0 && nonBlockingDirs.length === 0 && (
                            <div className="text-sm text-emerald-400">No active directives.</div>
                          )}
                        </div>

                        {/* DECISIONS REQUIRED */}
                        <div className="px-5 py-4 bg-slate-900/30">
                          <div className="text-sm font-semibold text-slate-300 mb-2">Decisions Required</div>
                          {b.no_decisions_required || decisions.length === 0 ? (
                            <div className="text-sm text-emerald-400">No owner decisions required at this time.</div>
                          ) : (
                            decisions.map((dec, i) => (
                              <div key={i} className="p-3 bg-purple-500/10 border border-purple-500/30 rounded-lg mb-2">
                                <div className="text-sm text-purple-200 font-semibold">{dec.decision}</div>
                                <div className="text-xs text-slate-400 mt-2">
                                  {dec.options?.map((opt, j) => <div key={j}>• {opt}</div>)}
                                </div>
                                {dec.ai_co_recommendation && (
                                  <div className="text-sm text-emerald-400 mt-2">Recommendation: {dec.ai_co_recommendation}</div>
                                )}
                              </div>
                            ))
                          )}
                        </div>

                        {/* SUPPORTING INTELLIGENCE - Collapsed */}
                        <div className="px-5 py-3">
                          <button 
                            onClick={() => toggleSection('intel')}
                            className="w-full flex items-center justify-between text-left hover:bg-slate-900/50 -mx-2 px-2 py-1 rounded transition-colors"
                          >
                            <span className="text-sm font-semibold text-slate-300">Supporting Intelligence ({intel.length} events)</span>
                            <ChevronDown className={`w-4 h-4 text-slate-500 transition-transform ${briefExpandedSections.intel ? 'rotate-180' : ''}`} />
                          </button>
                          {briefExpandedSections.intel && (
                            <div className="mt-3 space-y-1 text-sm">
                              {intel.map((event, i) => (
                                <div key={i} className="flex items-start gap-2 py-1 text-slate-400">
                                  <span className="text-slate-500 shrink-0 text-xs">{event.timestamp}</span>
                                  <span className="text-slate-500">—</span>
                                  <span className="text-slate-400 shrink-0 text-xs">{event.event_type}</span>
                                  <span className="text-slate-500">—</span>
                                  <span className="text-slate-300 text-xs">{event.description}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>

                    </div>
                  );
                }
                
                // Regular command/system entries
                return (
                  <div
                    key={entry.id}
                    className={`rounded-lg px-3 py-2.5 text-xs ${
                      entry.type === "command"
                        ? "bg-emerald-600/20 text-emerald-100 border border-emerald-500/30 ml-8"
                        : "bg-slate-800/80 text-slate-300 border border-slate-700/50"
                    }`}
                  >
                    {entry.type === "command" && (
                      <div className="text-xs text-emerald-400 font-semibold mb-1">COMMAND ISSUED</div>
                    )}
                    <div className="whitespace-pre-wrap leading-relaxed">{entry.content}</div>
                    <div className="text-xs text-slate-500 mt-1.5">
                      {new Date(entry.timestamp).toLocaleTimeString()}
                    </div>
                  </div>
                );
              })}
              {commandProcessing && (
                <div className="bg-slate-800/80 rounded-lg px-3 py-2.5 border border-slate-700/50">
                  <div className="flex items-center gap-2 text-xs text-slate-400">
                    <Loader2 className="w-3 h-3 animate-spin" />
                    Processing command...
                  </div>
                </div>
              )}
            </div>

            {/* Example Commands (Faint) - pinned above input */}
            <div className="flex-shrink-0 px-3 py-2 border-t border-slate-800/50 bg-slate-900/50">
              <div className="flex flex-wrap gap-2">
                {[
                  "What's going on with the Smith job?",
                  "Status on Miller project",
                  "Show mission status"
                ].map((example, i) => (
                  <button
                    key={i}
                    onClick={() => setCommandText(example)}
                    className="text-xs text-slate-500 hover:text-slate-300 transition-colors"
                  >
                    &quot;{example}&quot;
                  </button>
                ))}
              </div>
            </div>

            {/* Command Input - pinned at bottom */}
            <div className="flex-shrink-0 p-3 border-t border-slate-800 bg-slate-900">
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={commandText}
                  onChange={(e) => setCommandText(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleExecuteCommand()}
                  placeholder="Issue a command to the AI Commanding Officer..."
                  className="flex-1 bg-slate-800 border border-slate-700 rounded-lg px-3 py-2.5 text-xs text-white placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                  disabled={commandProcessing}
                />
                <button
                  onClick={handleExecuteCommand}
                  disabled={commandProcessing || !commandText.trim()}
                  className="px-3 py-2.5 bg-emerald-500 text-slate-900 rounded-lg hover:bg-emerald-400 transition-colors disabled:opacity-50 flex items-center gap-1.5 text-xs font-semibold"
                >
                  {commandProcessing ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Terminal className="w-4 h-4" />
                      EXECUTE
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
